import React, { useCallback } from 'react';
import classNames from 'classnames';
import { trackEvent } from '@surfline/web-common';
import { Box, Button, Typography } from '@mui/material';

import WavetrakLink from 'components/WavetrakLink/WavetrakLink';
import useMapControls from 'hooks/map/useMapControls';

import { MapLayer } from 'types/map';
import mapLayers from 'utils/mapLayers';
import styles from './MapControls.module.scss';

interface Props {
  activeMap: MapLayer;
  center?: any;
  className?: string;
  isGeonameMap?: boolean;
  subregion?: any;
  subregions?: any;
  subregionId?: string;
}

const MapControls: React.FC<Props> = ({
  activeMap,
  center,
  className,
  isGeonameMap,
  subregion,
  subregions,
  subregionId,
}) => {
  const { spotLink, buoyLink, chartLink, windStationLink } = useMapControls({
    activeMap,
    center,
    isGeonameMap,
    subregion,
    subregions,
    subregionId,
  });

  const handleOnClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      trackEvent('Chart Layer Updated', {
        layerType: 'Map',
        pageName: 'Map',
        from: activeMap,
        to: e?.currentTarget?.getAttribute('data-ref'),
      });
    },
    [activeMap],
  );

  return (
    <Box className={classNames(styles.spotMapBottomLeft, className)} data-testid="map-controls">
      <Button
        className={activeMap === mapLayers.SURF_SPOTS ? styles.activeLink : styles.mapControlsLink}
        color={activeMap === mapLayers.SURF_SPOTS ? undefined : 'dark'}
        component={WavetrakLink}
        data-ref="surf-spots"
        data-testid={
          activeMap === mapLayers.SURF_SPOTS
            ? 'map-surf-spots-button-active'
            : 'map-surf-spots-button'
        }
        disableRipple
        disableFocusRipple
        disableTouchRipple
        fullWidth={false}
        href={spotLink}
        onClick={handleOnClick}
        size="small"
        variant="primary"
      >
        <Typography variant="callout2">Surf spots</Typography>
      </Button>
      <Button
        className={activeMap === mapLayers.BUOYS ? styles.activeLink : styles.mapControlsLink}
        color={activeMap === mapLayers.BUOYS ? undefined : 'dark'}
        component={WavetrakLink}
        data-testid={
          activeMap === mapLayers.BUOYS ? 'map-wave-buoys-button-active' : 'map-wave-buoys-button'
        }
        data-ref="buoys"
        disableRipple
        disableFocusRipple
        disableTouchRipple
        fullWidth={false}
        href={buoyLink}
        onClick={handleOnClick}
        size="small"
        variant="primary"
      >
        <Typography variant="callout2">Wave buoys</Typography>
      </Button>
      <Button
        className={
          activeMap === mapLayers.WIND_STATIONS ? styles.activeLink : styles.mapControlsLink
        }
        color={activeMap === mapLayers.WIND_STATIONS ? undefined : 'dark'}
        component={WavetrakLink}
        data-testid={
          activeMap === mapLayers.WIND_STATIONS
            ? 'map-wind-stations-button-active'
            : 'map-wind-stations-button'
        }
        disableRipple
        disableFocusRipple
        disableTouchRipple
        href={windStationLink}
        data-ref="wind-stations"
        onClick={handleOnClick}
        size="small"
        variant="primary"
      >
        <Typography variant="callout2">Wind stations</Typography>
      </Button>

      <Button
        className={styles.mapControlsLink}
        color="dark"
        component={WavetrakLink}
        data-testid="map-charts-button"
        disableRipple
        disableFocusRipple
        disableTouchRipple
        href={chartLink}
        data-ref="maptiler-charts"
        isExternal
        onClick={handleOnClick}
        size="small"
        variant="primary"
      >
        <Typography variant="callout2">Charts</Typography>
      </Button>
    </Box>
  );
};

export default MapControls;
